import { apiClient } from './api.client';
import { Bot, BotMetrics, ClientMetrics, Lead, Conversation } from '@/types/api.types';

export interface BotResponse {
  id: string;
  name: string;
  status: 'active' | 'inactive' | 'maintenance';
  configuration?: {
    type: string;
    model?: string;
    language?: string;
    voice?: string;
    greeting?: string;
    max_duration?: number;
  };
  metrics?: {
    total_calls: number;
    success_rate: number;
    avg_duration: number;
    total_cost?: number;
    satisfaction_score?: number;
  };
  created_at: string;
  updated_at: string;
  last_active?: string;
}

export interface ClientMetricsResponse {
  totalConversations: number;
  activeLeads: number;
  conversionRate: number;
  totalCosts: number;
  trends?: {
    daily?: Array<{ date: string; value: number }>;
    weekly?: Array<{ week: string; value: number }>;
    monthly?: Array<{ month: string; value: number }>;
  };
  comparisons?: {
    lastWeek?: { value: number; change: number };
    lastMonth?: { value: number; change: number };
  };
}

export interface ConversationResponse {
  id: string;
  lead_id: string;
  lead?: {
    name: string;
    phone?: string;
    email?: string;
  };
  sender_type: 'bot' | 'user' | 'system';
  content: string;
  duration?: number;
  status: 'completed' | 'in_progress' | 'failed';
  created_at: string;
  updated_at: string;
}

class ClientApiService {
  /**
   * Get client metrics with trends and comparisons
   */
  async getClientMetrics(clientId: string): Promise<ClientMetricsResponse> {
    const response = await apiClient.get<ClientMetricsResponse>(`/clients/${clientId}/metrics`);
    return response.data;
  }

  /**
   * Get bots for a specific client
   */
  async getClientBots(clientId: string): Promise<BotResponse[]> {
    const response = await apiClient.get<{
      success: boolean;
      data: Array<{
        id: string;
        name: string;
        status: 'active' | 'inactive' | 'maintenance';
        calls_today: number;
        success_rate: number;
      }>;
      pagination?: {
        total: number;
        limit: number;
        offset: number;
      };
    }>(`/clients/${clientId}/bots`);
    
    // Map the API response to BotResponse format
    return response.data.data.map(bot => ({
      id: bot.id,
      name: bot.name,
      status: bot.status,
      metrics: {
        total_calls: bot.calls_today,
        success_rate: bot.success_rate,
        avg_duration: 0,
      },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));
  }

  /**
   * Get a specific bot for a client
   */
  async getClientBot(clientId: string, botId: string): Promise<BotResponse> {
    const response = await apiClient.get<BotResponse>(`/clients/${clientId}/bots/${botId}`);
    return response.data;
  }

  /**
   * Get client conversations with pagination
   * Backend uses page-based pagination, frontend expects offset-based
   */
  async getClientConversations(
    clientId: string,
    params?: {
      limit?: number;
      offset?: number;
      start_date?: string;
      end_date?: string;
      lead_id?: string;
      sender_type?: 'bot' | 'user' | 'system';
    }
  ): Promise<{ data: ConversationResponse[]; total: number; limit: number; offset: number }> {
    // Convert offset to page for backend API
    const page = params?.offset ? Math.floor(params.offset / (params?.limit || 20)) + 1 : 1;
    const backendParams = {
      page,
      limit: params?.limit || 20,
      ...(params?.start_date && { startDate: params.start_date }),
      ...(params?.end_date && { endDate: params.end_date }),
      ...(params?.lead_id && { lead_id: params.lead_id }),
      ...(params?.sender_type && { sender_type: params.sender_type })
    };
    
    const response = await apiClient.get(`/clients/${clientId}/conversations`, { params: backendParams });
    
    // Transform backend response to frontend format
    const backendData = response.data.success ? response.data.data : response.data;
    return {
      data: backendData.items || backendData.data || [],
      total: backendData.total || 0,
      limit: backendData.page_size || params?.limit || 20,
      offset: params?.offset || 0
    };
  }

  /**
   * Get client leads with pagination - Using /clients/{id}/leads endpoint
   * Supports all filtering options from the backend API
   */
  async getClientLeads(
    clientId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      lead_status?: string;
      email?: string;
      phone?: string;
      full_name?: string;
      budget?: string;
      date_from?: string;
      date_to?: string;
      last_message_date_start?: string;
      last_message_date_end?: string;
      sort_by?: 'created_at' | 'updated_at' | 'lead_status' | 'last_message_date' | 'full_name' | 'budget';
      sort_order?: 'asc' | 'desc';
    }
  ): Promise<{ data: Lead[]; total: number; page: number; total_pages: number; page_size: number }> {
    // Build query parameters for the endpoint
    const queryParams = new URLSearchParams();
    
    // Add pagination parameters (page-based as per API)
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', Math.min(params.limit, 50).toString()); // Max 50 per API docs
    
    // Add filter parameters
    if (params?.search) queryParams.append('search', params.search);
    if (params?.lead_status) queryParams.append('lead_status', params.lead_status);
    if (params?.email) queryParams.append('email', params.email);
    if (params?.phone) queryParams.append('phone', params.phone);
    if (params?.full_name) queryParams.append('full_name', params.full_name);
    if (params?.budget) queryParams.append('budget', params.budget);
    
    // Add date range filters (using the correct parameter names)
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.last_message_date_start) queryParams.append('last_message_date_start', params.last_message_date_start);
    if (params?.last_message_date_end) queryParams.append('last_message_date_end', params.last_message_date_end);
    
    // Add sorting parameters
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);
    
    // Use the correct /clients/{id}/leads endpoint
    const response = await apiClient.get(`/clients/${clientId}/leads?${queryParams.toString()}`);
    
    // Transform backend response to frontend format
    const backendData = response.data.success ? response.data.data : response.data;
    
    return {
      data: backendData.items || backendData.data || [],
      total: backendData.total || 0,
      page: backendData.page || params?.page || 1,
      total_pages: backendData.total_pages || 1,
      page_size: backendData.page_size || params?.limit || 20
    };
  }

  /**
   * Get all clients (Admin/Developer only)
   * Viewers with client_project_id see only their client
   *
   * Endpoint: GET /api/v1/clients
   * Cache: 1 minute
   * Sort: created_at DESC
   */
  async getAllClients(params?: {
    status?: 'active' | 'inactive' | 'maintenance';
  }): Promise<{ clients: any[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.status) {
      queryParams.append('status', params.status);
    }

    const url = `/clients${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<{
      success: boolean;
      data: {
        clients: any[];
        total: number;
      };
    }>(url);

    return response.data.data;
  }

  /**
   * Get client details - Updated for new API structure with vapi_tag
   */
  async getClientDetails(clientId: string): Promise<{
    id: string;
    name: string;
    supabase_project_id: string;
    vault_secret_name: string;
    project_type: 'chatbot' | 'voicebot';
    status: 'active' | 'inactive';
    created_at: string;
    updated_at: string;
    vapi_tag: string;
    stats: {
      lead_count: number;
      conversation_count: number;
    };
  }> {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        client: {
          id: string;
          name: string;
          supabase_project_id: string;
          vault_secret_name: string;
          project_type: 'chatbot' | 'voicebot';
          status: 'active' | 'inactive';
          created_at: string;
          updated_at: string;
          vapi_tag: string;
          stats: {
            lead_count: number;
            conversation_count: number;
          };
        };
      };
    }>(`/clients/${clientId}`);

    // Return the nested client object directly
    return response.data.data.client;
  }

  /**
   * Create a new client
   * Admin role required
   */
  async createClient(clientData: {
    name: string;
    supabase_project_id: string;
    vault_secret_name: string;
    project_type: 'chatbot' | 'voicebot';
    status?: 'active' | 'inactive' | 'maintenance';
  }): Promise<{
    id: string;
    name: string;
    supabase_project_id: string;
    vault_secret_name: string;
    project_type: 'chatbot' | 'voicebot';
    status: 'active' | 'inactive' | 'maintenance';
    vapi_tag: string | null;
    created_at: string;
    updated_at: string;
  }> {
    const response = await apiClient.post<{
      success: boolean;
      data: {
        client: {
          id: string;
          name: string;
          supabase_project_id: string;
          vault_secret_name: string;
          project_type: 'chatbot' | 'voicebot';
          status: 'active' | 'inactive' | 'maintenance';
          vapi_tag: string | null;
          created_at: string;
          updated_at: string;
        };
      };
    }>('/clients', clientData);

    return response.data.data.client;
  }

  /**
   * Update an existing client
   * Admin role required
   */
  async updateClient(
    clientId: string,
    updates: {
      name?: string;
      supabase_project_id?: string;
      vault_secret_name?: string;
      project_type?: 'chatbot' | 'voicebot';
      status?: 'active' | 'inactive' | 'maintenance';
      vapi_tag?: string;
    }
  ): Promise<{
    id: string;
    name: string;
    supabase_project_id: string;
    vault_secret_name: string;
    project_type: 'chatbot' | 'voicebot';
    status: 'active' | 'inactive' | 'maintenance';
    vapi_tag: string | null;
    created_at: string;
    updated_at: string;
  }> {
    const response = await apiClient.put<{
      success: boolean;
      data: {
        client: {
          id: string;
          name: string;
          supabase_project_id: string;
          vault_secret_name: string;
          project_type: 'chatbot' | 'voicebot';
          status: 'active' | 'inactive' | 'maintenance';
          vapi_tag: string | null;
          created_at: string;
          updated_at: string;
        };
      };
    }>(`/clients/${clientId}`, updates);

    return response.data.data.client;
  }

  /**
   * Delete a client
   * Admin role required
   * Safety validation: Client cannot be deleted if it has associated leads or conversations
   */
  async deleteClient(clientId: string): Promise<{
    message: string;
    deletedClientId: string;
    deletedClientName: string;
  }> {
    const response = await apiClient.delete<{
      success: boolean;
      data: {
        message: string;
        deletedClientId: string;
        deletedClientName: string;
      };
    }>(`/clients/${clientId}`);

    return response.data.data;
  }
}

export const clientApiService = new ClientApiService();